import { useEffect, useCallback } from 'react'

import { useUserInteraction } from '@/hooks/useUserInteraction'
import { Keys } from '@/platform/platform'
import { isWebos } from '@/utils/utils'

export function useBackKeyAnalytics(
  inPagePlayer: boolean,
  sendBackPressAnalyticsEvent: () => void,
) {
  const onKeyPress = useCallback(
    (e: KeyboardEvent) => {
      if (e.keyCode === Keys.BACKSPACE || e.keyCode === Keys.BACK) {
        e.preventDefault()
        sendBackPressAnalyticsEvent()
      }
    },
    [sendBackPressAnalyticsEvent],
  )

  useEffect(() => {
    if (!inPagePlayer) {
      return
    }
    window.addEventListener('keyup', onKeyPress)
    return () => {
      window.removeEventListener('keyup', onKeyPress)
    }
  }, [inPagePlayer, onKeyPress])
}

export function usePreventWebosScreenSaver({
  isStageHome,
  inPagePlayer,
  isVideoPlaying,
  isFullscreen,
  scrollToSection,
  setIsFullscreen,
  sendFullScreenAnalyticsEvent,
}: {
  isStageHome: boolean
  inPagePlayer: boolean
  isVideoPlaying: boolean
  isFullscreen: boolean
  scrollToSection?: () => void
  setIsFullscreen: (v: boolean) => void
  sendFullScreenAnalyticsEvent: () => void
}) {
  const isWebosPlatform = isWebos()

  const enableUserInteractionTracking =
    isWebosPlatform &&
    !isStageHome &&
    inPagePlayer &&
    isVideoPlaying &&
    !isFullscreen

  const onUserInactivityTimeout = useCallback(() => {
    scrollToSection?.()
    setIsFullscreen(true)
    sendFullScreenAnalyticsEvent()
  }, [scrollToSection, setIsFullscreen, sendFullScreenAnalyticsEvent])

  useUserInteraction(onUserInactivityTimeout, enableUserInteractionTracking)
}
